import { useState, useEffect, useRef } from 'react';
import {
    Button,
    Container,
    PageBanner,
} from '~/Common';
import {
    Badge,
    Box,
    Flex,
    Grid,
    Heading,
    NavLink,
    Text
} from '@theme-ui/components';
import { Filters } from '~/Research';
import { Icon } from 'assets/Icon';
import { DashboardLayout } from '~/Layouts';
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useResponsiveValue } from '@theme-ui/match-media';
import { appendAsxResearchReportsAsync, appendUnlistedResearchReportsAsync, getAsxResearchReportsAsync, getResearchReports, getUnlistedResearchReportsAsync } from 'features/research/researchSlice';
import { ResearchCard } from '~/Research';
import { getCategoriesAsync } from 'features/category/categorySlice';

const mapStateToProps = (state) => {
    return { categories: state.categories, research: state.research };
}

const ResearchReportDashboard = ({ categories, research }) => {
    
    const defaultResults = new Array(9).fill(null);
    const { t } = useTranslation();
    useDocumentTitle(t('Research'));
    const dispatch = useDispatch();
    const asxRef = useRef(null);
    const unlistedRef = useRef(null);
    const [asxResults, setAsxResults] = useState(defaultResults);
    const [unlistedResults, setUnlistedResults] = useState(defaultResults);
    const [isFiltering, setIsFiltering] = useState(false);
    const [loading, setLoading] = useState(true);
    const [liveFiltersAsx, setLiveFiltersAsx] = useState({ type: 'asx' });
    const [liveFiltersUnlisted, setLiveFiltersUnlisted] = useState({ type: 'unlisted' });
    const filterRef = useRef();
    const isMobile = useResponsiveValue([true, true, true, false]);

    const handleSubmit = (values) => {
        setIsFiltering(true);
        const asxPayload = getResearchReports({
            params: {
                page: 0,
                category: values?.category ?? null,
                sortBy: values?.sortBy ?? null,
                type: 'asx'
            }
        });

        const unlistedPayload = getResearchReports({
            params: {
                page: 0,
                category: values?.category ?? null,
                sortBy: values?.sortBy ?? null,
                type: 'unlisted'
            }
        });

        setLiveFiltersAsx({
            page: 0,
            category: values?.category ?? null,
            sortBy: values?.sortBy ?? null,
            type: 'asx'
        });
        setLiveFiltersUnlisted({
            page: 0,
            category: values?.category ?? null,
            sortBy: values?.sortBy ?? null,
            type: 'unlisted'
        });

        setLoading(true);
        dispatch(getAsxResearchReportsAsync(asxPayload));
        dispatch(getUnlistedResearchReportsAsync(unlistedPayload));
        return false;
    };

    const resetFilters = (e) => {
        setLoading(true);
        e.preventDefault();
        filterRef.current.resetForm();
        setIsFiltering(false);
        const payloadAsx = getResearchReports({
            params: {
                type: 'asx'
            }
        });
        setAsxResults(defaultResults);
        dispatch(getAsxResearchReportsAsync(payloadAsx));

        const payloadUnlisted = getResearchReports({
            params: {
                type: 'unlisted'
            }
        });
        setUnlistedResults(defaultResults);
        dispatch(getUnlistedResearchReportsAsync(payloadUnlisted));
        setLiveFiltersAsx({});
        setLiveFiltersUnlisted({});
        setLoading(false);
    };

    useEffect(() => {
        if (categories?.categories?.status === 'idle') {
            dispatch(getCategoriesAsync());
        }
        if (research?.asx?.status === 'idle') {
            dispatch(getAsxResearchReportsAsync());
        }
        if (research?.asx?.status === 'attempted') {
            setAsxResults(research?.asx?.results);
            setLoading(false);
        }
        if (research?.unlisted?.status === 'idle') {
            dispatch(getUnlistedResearchReportsAsync());
        }
        if (research?.unlisted?.status === 'attempted') {
            setUnlistedResults(research?.unlisted?.results);
            setLoading(false);
        }
    }, [dispatch, research, categories]);

    const buttonStyles = {
        marginX: 'auto',
        width: 'fit-content',
        height: 'auto',
        alignItems: 'center',
        justifyContent: 'center',
        py: '14px',
        px: '24px',
        lineHeight: 1
    };

    const handleShowMoreAsx = () => {
        if (research.asx?.meta?.current_page >= research.asx?.meta?.last_page) {
            return;
        }

        setLiveFiltersAsx((prev) => ({
            ...prev,
            page: (research.asx?.meta?.current_page + 1),
        }));
        const newFilters = { ...liveFiltersAsx, page: (research.asx?.meta?.current_page + 1) };
        console.log('newFilters', newFilters);
        const payload = getResearchReports({ params: newFilters });
        console.log('payload', payload);
        setAsxResults(() => defaultResults);
        setLoading(true);
        dispatch(appendAsxResearchReportsAsync(payload));
    }

    const handleShowMoreUnlisted = () => {
        if (research.unlisted?.meta?.current_page >= research.unlisted?.meta?.last_page) {
            return;
        }

        setLiveFiltersUnlisted((prev) => ({
            ...prev,
            page: (research.unlisted?.meta?.current_page + 1),
        }));
        const newFilters = { ...liveFiltersUnlisted, page: (research.unlisted?.meta?.current_page + 1) };
        const payload = getResearchReports({ params: newFilters });
        setUnlistedResults(() => defaultResults);
        setLoading(true);
        dispatch(appendUnlistedResearchReportsAsync(payload));
    }

    return (
        <DashboardLayout dark={true} hideCta={true} p={0}>
            <PageBanner img={'industries'} sx={{ paddingBottom: 0 }}>
                <Badge mt={4} px={3} variant="header" >{t('Stay ahead with Liquidity')}</Badge>
                <Heading mt={3} mb={2} as="h2" variant="industry_page_title">{t('Enhancing Insights with Dynamic Research')}</Heading>

                <Box variant="layouts.default" mt={2}>
                    <Filters handleSubmit={handleSubmit} formRef={filterRef} isMobile={isMobile} />
                </Box>
            </PageBanner>
            <Container sx={{ position: 'relative', zIndex: 1 }} variant="layouts.default">

                {/* ASX Reports */}
                <Heading as="h1" variant="industryH2" mt={5} mb={3}>{t(`ASX Research`)}</Heading>
                <Flex ref={asxRef} mb={4} sx={{
                    flexDirection: 'column',
                    position: 'relative',
                }}>
                    {asxResults?.length > 0 ?
                        <>
                            <Grid sx={{
                                gridGap: 4,
                                gridTemplateColumns: ['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)', null, 'repeat(4, 1fr)'],
                                gridTemplateRows: '1fr',
                                width: '100%',
                                maxWidth: '100%',
                                overflowX: 'auto',
                                pt: '10px',
                                pb: '30px',
                                px: 3,
                                mx: -3,
                                boxSizing: 'content-box',
                            }}>
                                {asxResults.map((report, key) => (
                                    <Box px={[3, 0]} key={`category-result${key}`}>
                                        <ResearchCard report={report} company={report?.company} variant={'researchDashboard'} />
                                    </Box>
                                ))}
                            </Grid>

                            {(!loading && asxResults?.length < research?.asx?.meta?.total) && 
                                <Button onClick={handleShowMoreAsx} variant="primary" sx={buttonStyles} mr={3} mt={4} >{t('Show me more')}</Button>
                            }
                        </>
                    : null}

                    {(!loading && asxResults?.length === 0) &&
                        <Box px={2}>
                            <Flex sx={{
                                py: 5,
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column'
                            }}>
                                <Icon icon={isFiltering ? 'error' : 'lock-thin'} color="tealDark" mb={'28px'} width="84px" />
                                <Heading variant="h2" sx={{ fontSize: '36px' }}>{t('researchDashboard.notFoundHeading')}</Heading>
                                <Text mt={1} variant="subtext">{isFiltering ? t('researchDashboard.filteringNotFound') : t('researchDashboard.notFound')}</Text>
                                {isFiltering &&
                                    <Button
                                        as={NavLink}
                                        onClick={resetFilters}
                                        sx={{
                                            minWidth: '160px',
                                            mt: '56px'
                                        }}
                                    >
                                        Reset filters
                                    </Button>
                                }
                            </Flex>
                        </Box>
                    }
                </Flex>

                {/* Unlisted Reports */}
                <Heading as="h1" variant="industryH2" mt={5} mb={3}>{t(`Unlisted Research`)}</Heading>
                <Flex ref={unlistedRef} mb={4} sx={{
                    flexDirection: 'column',
                    position: 'relative',
                }}>
                    {unlistedResults?.length > 0 ?
                        <>
                            <Grid sx={{
                                gridGap: 4,
                                gridTemplateColumns: ['1fr', null, 'repeat(3, 1fr)', null, 'repeat(4, 1fr)'],
                                gridTemplateRows: '1fr',
                                width: '100%',
                                overflowX: 'auto',
                                pt: '10px',
                                pb: '30px',
                                px: 3,
                                mx: -3,
                                boxSizing: 'content-box',
                            }}>
                                {unlistedResults.map((report, key) => (
                                    <Box px={[3, 0]} key={`category-result${key}`}>
                                        <ResearchCard report={report} company={report?.company} variant={'researchDashboard'} />
                                    </Box>
                                ))}
                            </Grid>

                            {(!loading && unlistedResults?.length < research?.unlisted?.meta?.total) && 
                                <Button onClick={handleShowMoreUnlisted} variant="primary" sx={buttonStyles} mr={3} mt={4}>{t('Show me more')}</Button>
                            }
                        </>
                    : null}

                    {(!loading && unlistedResults?.length === 0) &&
                        <Box px={2}>
                            <Flex sx={{
                                py: 5,
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column'
                            }}>
                                <Icon icon={isFiltering ? 'error' : 'lock-thin'} color="tealDark" mb={'28px'} width="84px" />
                                <Heading variant="h2" sx={{ fontSize: '36px' }}>{t('researchDashboard.notFoundHeading')}</Heading>
                                <Text mt={1} variant="subtext">{isFiltering ? t('researchDashboard.filteringNotFound') : t('researchDashboard.notFound')}</Text>
                                {isFiltering &&
                                    <Button
                                        as={NavLink}
                                        onClick={resetFilters}
                                        sx={{
                                            minWidth: '160px',
                                            mt: '56px'
                                        }}
                                    >
                                        Reset filters
                                    </Button>
                                }
                            </Flex>
                        </Box>
                    }
                </Flex>


                {/* Industries */}
                <Heading as="h1" variant="industryH2" mt={5}>{t(`Explore Industries`)}</Heading>
                <Flex mt={3} sx={{ gap: '22px', flexWrap: 'wrap', overflowX: isMobile ? 'scroll' : 'auto', maxWidth: '100vw' }}>
                    {categories?.categories?.results?.map((category, key) => (
                        <Box
                            as={NavLink}
                            href={`/industries/${category.slug}`}
                            sx={{
                                paddingY: '0px',
                                backgroundColor: '#00000A10',
                                borderRadius: '16px',
                                width: 'auto',
                                flexShrink: 0,
                                '&:hover': {
                                    backgroundColor: '#00000A20',
                                },
                            }} 
                            key={`category-result${key}`}
                        >
                            <Box py="20px" px="20px"  sx={{ color: '#00000A', fontWeight: 'normal' }}>
                                {category.name}
                            </Box>
                        </Box>
                    ))}
                </Flex>
            </Container>
        </DashboardLayout>
    );
};

export default connect(mapStateToProps, null)(ResearchReportDashboard);